<template>
    <div class="checkout-activity-form">
        <FormV2
            :id="`form_${product.Id}`"
            :key="formId"
            :validationSchema="validationSchema"
            :initialValues="initialValues"
            :onValidChange="handleValidChange">
            <div v-if="product?.Tickets.length" class="checkout-activity-form-tickets">
                <Ticket
                    v-for="(ticket, index) in product.Tickets"
                    :key="index"
                    :productId="product.Id"
                    :ticket="ticket"
                    :index="index"
                    :activity="product"
                    @addCustomField="(custom: CustomField) => addCustomField(custom)"
                    @removeCustomField="(custom: CustomField) => removeCustomField(custom)" />
            </div>
        </FormV2>
    </div>
</template>

<script lang="ts">
    import { computed, defineComponent, PropType, ref, watch } from 'vue';
    import { ActivityProduct } from '@/api/Cart/cart.interfaces';
    import { useStore } from '@/store';
    import { ValidationSchema } from '@ui/components/FormV2/Validations/Validations.interfaces';
    import { FormValues } from '@ui/components/FormV2/Form.interfaces';
    import {
        getMandatoryFields,
        CustomField,
    } from '@/components/checkoutPageV3/Sections/Products/Activity/Activity.helpers';
    import { generateUUID } from '@shared/utils/uuid';
    import { AgeRangeDefinitions } from '@/api/Checkout/checkout.interfaces';
    import { getAgeConfigByProduct } from '@/helpers/dobValidation';
    import isNil from 'lodash/isNil';
    import isEqual from 'lodash/isEqual';
    import FormV2 from '@ui/components/FormV2/Form.vue';
    import Ticket from '@/components/checkoutPageV3/Sections/Products/Activity/Form/Ticket/Ticket.vue';

    export default defineComponent({
        name: 'ActivityForm',
        components: { Ticket, FormV2 },
        props: {
            sectionId: { type: String, required: true },
            product: {
                type: Object as PropType<ActivityProduct>,
                required: true,
            },
        },
        setup(props) {
            const store = useStore();

            const formId = ref<string | undefined>(undefined);
            const ticketFields = ref<any[][] | undefined>(undefined);
            const initialValues = ref<FormValues | undefined>(undefined);
            const validationSchema = ref<ValidationSchema | undefined>(undefined);

            const ageDefinitions = computed<AgeRangeDefinitions>(
                () => store.getters['content/getCheckoutAgeRangeDefinitions']
            );

            const ageConfig = computed(() =>
                getAgeConfigByProduct(ageDefinitions.value, props.product.Type, null)
            );

            function handleValidChange(valid: boolean) {
                store.dispatch('checkout/setValid', {
                    id: props.sectionId,
                    valid,
                });
            }

            function addCustomField(custom: CustomField) {
                if (ticketFields.value) {
                    //We create a new temp fields array to reassign it to ticketFields ref and make the watcher react
                    const tempFields = [...ticketFields.value];

                    const fieldIndex = tempFields[custom.index].findIndex(
                        (f) => f.prop === custom.field.prop
                    );

                    if (fieldIndex === -1) {
                        tempFields[custom.index].push(custom.field);
                        ticketFields.value = tempFields;
                    }
                }
            }

            function removeCustomField(custom: CustomField) {
                if (ticketFields.value) {
                    //We create a new temp fields array to reassign it to ticketFields ref and make the watcher react
                    const tempFields = [...ticketFields.value];

                    const fieldIndex = tempFields[custom.index].findIndex(
                        (f) => f.prop === custom.field.prop
                    );

                    if (fieldIndex > -1) {
                        tempFields[custom.index].splice(fieldIndex, 1);
                        ticketFields.value = tempFields;
                    }
                }
            }

            function getFields(product: ActivityProduct) {
                const date = new Date(product.Date);

                return product.Tickets.map((ticket, index) => {
                    return getMandatoryFields(ticket.Pax, index, ageConfig.value, date);
                });
            }

            function getValidationSchema(fields: any[][]) {
                const schema: ValidationSchema = {};

                fields.flat().forEach((f) => {
                    const name = `${f.obj}_${f.prop}`;
                    schema[name] = f.validation;
                });

                return schema;
            }

            function getInitialValues(fields: any[][]) {
                const values: FormValues = {};

                fields.forEach((field) => {
                    values[field[0].obj] = {};

                    field.forEach((f) => {
                        values[field[0].obj][f.prop] = f.value;
                    });
                });

                return values;
            }

            function setTicketFields(product: ActivityProduct) {
                ticketFields.value = getFields(product);
            }

            function setInitialValues(fields: any[][] | undefined) {
                if (!isNil(fields)) {
                    initialValues.value = getInitialValues(fields);
                }
            }

            function setSchema(fields: any[][] | undefined) {
                if (!isNil(fields)) {
                    validationSchema.value = getValidationSchema(fields);
                }
            }

            function setFormId() {
                formId.value = generateUUID();
            }

            watch(
                props.product,
                (newValue, oldValue) => {
                    if (!isNil(newValue) && !isEqual(newValue, oldValue)) {
                        setTicketFields(newValue);
                    }
                },
                { immediate: true }
            );

            watch(
                ticketFields,
                () => {
                    setInitialValues(ticketFields.value);
                    setSchema(ticketFields.value);
                    setFormId();
                },
                { immediate: true }
            );

            return {
                formId,
                initialValues,
                validationSchema,
                handleValidChange,
                addCustomField,
                removeCustomField,
            };
        },
    });
</script>

<style lang="scss">
    @forward 'Form.scss';
</style>
